import {Typography, WithStyles, withStyles} from '@material-ui/core';
import Button from "@components/common/button/Button";
import TranslateNew from '@translate/TranslateNew';
import * as React from "react";
import {FormEvent} from "react";
import Input from "@components/common/input/input.component";
import {AddChildStyles} from "@components/onboarding/steps/StepAddChild/addChild.styles";
import ProgressBar from "@components/common/progressBar/progressBar";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@components/common/iconButton/iconButton.component";
import {signupAddChild} from "@services/family/family.actions";
import {connect} from "react-redux";

const tn: TranslateNew = TranslateNew.getInstance();

interface IAddChildProps extends WithStyles<typeof AddChildStyles> {
    nextStep: (customStep?: number) => void;
    progress?: number | undefined;
    addChild: (data: object) => void;
}

interface IState {
    formValues: {
        childs: Array<{
            firstName: string,
            yearBorn: number
        }>;
    };
    formErrors: {
        childs?: Array<{
            firstName?: string | undefined,
            yearBorn?: string | undefined
        }> | string | undefined;
    };
}

class AddChild extends React.Component<IAddChildProps, IState> {

    constructor(props: any) {
        super(props);

        this.state = {
            formValues: {
                childs: [{firstName: '', yearBorn: new Date().getFullYear() - 3}],
            },
            formErrors: {
                childs: undefined,
            },
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleInputBlur = this.handleInputBlur.bind(this);
        this.validateChilds = this.validateChilds.bind(this);
        this.handleDeleteChild = this.handleDeleteChild.bind(this);
        this.handleAddChild = this.handleAddChild.bind(this);
    }

    public render() {
        const {formErrors, formValues} = this.state;
        const style = this.props.classes;
        const childsError = formErrors.childs && typeof formErrors.childs === 'string' ? formErrors.childs : undefined;

        return (
            <div className={style.root}>
                <div className={style.background}>
                    <div className={style.circle}/>
                </div>
                <div className={style.container}>
                    {this.props.progress && (<div className={style.topBar}>
                        <ProgressBar value={this.props.progress}/>
                    </div>)}
                    <div className={style.topContent}>
                        <Typography variant="h2">{tn.lang.onbChildTitle}</Typography>
                    </div>
                    <form className={style.form} onSubmit={this.handleSubmit}>
                        <div className={style.formMiddleContent}>
                            {formValues.childs.map((child, index) => {
                                const error = formErrors.childs && Array.isArray(formErrors.childs)
                                    ? formErrors.childs[index] : undefined;
                                const field = `childs.${index}`;
                                const firstNameField = `childs.${index}.firstName`;
                                const yearBornField = `childs.${index}.yearBorn`;

                                return (<div key={field} className={style.childCard}>
                                        <Input
                                            id={firstNameField}
                                            name={firstNameField}
                                            type="text"
                                            autoComplete="first-name"
                                            onChange={this.handleInputChange}
                                            onBlur={this.handleInputBlur}
                                            value={child.firstName.replace(/"/g, "'")}
                                            label={tn.lang.labelFirstName}
                                            error={error?.firstName}
                                        />
                                        <Input
                                            id={yearBornField}
                                            name={yearBornField}
                                            type="number"
                                            inputMode="numeric"
                                            pattern='[0-9]*'
                                            autoComplete="years"
                                            onChange={this.handleInputChange}
                                            onBlur={this.handleInputBlur}
                                            value={child.yearBorn}
                                            label={tn.lang.labelDateOfBirth}
                                            error={error?.yearBorn}
                                        />
                                        {formValues.childs.length > 1 && (
                                            <IconButton
                                                className={style.deleteChildButton}
                                                onClick={(event) => this.handleDeleteChild(index)}
                                                color="primary">
                                                <CloseIcon fontSize="small"/>
                                            </IconButton>
                                        )}
                                    </div>
                                );
                            })}
                            {childsError && <Typography className={style.errorMessage}>{childsError}</Typography>}
                        </div>
                        <div className={style.formBottomContent}>
                            <Button
                                type="button"
                                variant='contained'
                                color='secondary'
                                size="large"
                                fullWidth={true}
                                onClick={this.handleAddChild}
                            >
                                <span style={{flex: 1}}>{tn.lang.addChild}</span>
                                <AddIcon fontSize="small"/>
                            </Button>
                            <Button
                                type="submit"
                                variant='contained'
                                color='primary'
                                size="large"
                                fullWidth={true}
                            >
                                {tn.lang.continue}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    private handleAddChild = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        this.setState((prevState) => {
            const newChilds = [...prevState.formValues.childs];
            newChilds.push({firstName: '', yearBorn: new Date().getFullYear() - 3});

            return {
                formValues: {
                    ...prevState.formValues,
                    childs: newChilds,
                },
                formErrors: {
                    ...prevState.formErrors,
                    childs: undefined
                },
            };
        });
    }

    private handleDeleteChild = (index: number) => {
        this.setState((prevState) => {
            const newChilds = [...prevState.formValues.childs];
            newChilds.splice(index, 1);

            return {
                formValues: {
                    ...prevState.formValues,
                    childs: newChilds,
                },
                formErrors: {
                    ...prevState.formErrors,
                    childs: undefined
                },
            };
        });
    }

    private validateChilds = (): boolean => {
        const {formValues} = this.state;
        const childErrors: Array<{
            firstName?: string | undefined,
            yearBorn?: string | undefined
        }> | string | undefined = [];

        if (!formValues.childs) {
            this.setState({
                formErrors: {
                    childs: tn.lang.validationChildRequired
                },
            });
            return false;
        } else if (formValues.childs.length < 1) {
            this.setState({
                formErrors: {
                    childs: tn.lang.validationChildMin(1)
                },
            });
            return false;
        } else {
            let hasErrors = false;
            for (const child of formValues.childs) {
                const errors: {
                    firstName?: string | undefined,
                    yearBorn?: string | undefined
                } = {};

                errors.firstName = this.validateFirstName(child.firstName);
                errors.yearBorn = this.validateDateOfBirthdayYear(child.yearBorn);

                hasErrors = !!(errors.firstName || errors.yearBorn);

                childErrors.push(errors);
            }

            if (hasErrors) {
                this.setState({
                    formErrors: {
                        childs: childErrors,
                    },
                });
                return false;
            }
        }

        this.setState({
            formErrors: {
                childs: undefined,
            },
        });
        return true;
    }

    private validateFirstName = (value: string): string | undefined => {
        if (!value) {
            return tn.lang.validationFirstNameRequired;
        } else if (value.length < 2) {
            return tn.lang.validationFirstNameInvalid;
        }

        return undefined;
    }

    private validateDateOfBirthdayYear = (year: number): string | undefined => {
        const currentYear = new Date().getFullYear();
        const minYear = currentYear - 20;

        if (year < minYear || year > currentYear) {
            return tn.lang.validationYearInvalid;
        }

        return undefined;
    }

    private handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const isChildsValid = this.validateChilds();

        if (isChildsValid) {
            try {
                this.props.addChild(this.state.formValues);
            } catch (error) {
                console.error(error);
            }
        }
    }

    private handleInputBlur = (event: React.FormEvent<HTMLInputElement>) => {
        const {name} = event.currentTarget;
        if (name.includes('childs')) {
            this.validateChilds();
        }
    }

    private handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value, type, checked} = event.target;
        const inputValue = type === 'checkbox' ? checked : value;

        if (name.includes('childs')) {
            const [parentName, index, fieldName] = name.split('.');
            this.setState((prevState) => {
                const parentList = [...prevState.formValues[parentName]];
                parentList[index][fieldName] = inputValue;
                return {
                    formValues: {
                        ...prevState.formValues,
                        [parentName]: parentList,
                    },
                    formErrors: {
                        [parentName]: undefined,
                    },
                };
            });
        } else {
            this.setState((prevState) => ({
                formValues: {
                    ...prevState.formValues,
                    [name]: inputValue,
                },
                formErrors: {
                    [name]: undefined,
                },
            }));
        }

    }
}

const mapDispatchToProps = {
    addChild: signupAddChild,
};

export default connect(null, mapDispatchToProps)(withStyles(AddChildStyles)(AddChild));



// WEBPACK FOOTER //
// ./src/components/onboarding/steps/StepAddChild/addChild.component.tsx